import * as React from 'react'
import { format, startOfToday, startOfTomorrow, endOfTomorrow, addDays, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns'
import {
  Plus,
  Search,
  RefreshCw,
  ClipboardList,
  Calendar,
  MapPin,
  User,
  Phone,
  DollarSign,
  Edit,
  Trash2,
  Car,
  X,
  Sparkles,
  MoreHorizontal,
  FileImage,
  FileText,
  ImagePlus,
  Loader2,
  Play,
  UserCheck,
  CheckCircle,
  LogOut,
  MessageSquare,
  Copy,
  Check,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  WifiOff,
} from 'lucide-react'
import TableLoading from '@/components/ui/TableLoading'
import OrderDrawer from '@/components/orders/OrderDrawer'
import CustomerDrawer from '@/components/customers/CustomerDrawer'
import AIOrderDrawer from '@/components/orders/AIOrderDrawer'
import BulkOrdersConfirmDrawer from '@/components/orders/BulkOrdersConfirmDrawer'

// 穩定的空陣列參照：避免每次 render 都傳一個新的 [] 字面值給子元件，
// 觸發子元件裡依賴該 prop 參照的 useEffect（例如清空表單）
const EMPTY_TENANTS: never[] = []
import DocumentModal from '@/components/orders/DocumentModal'
import SignatureModal from '@/components/orders/SignatureModal'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { createExternalSignLink, getOrders, getOrder, createOrder, updateOrder, deleteOrder, uploadSignature, uploadCustomSignImage, generateDispatchDocument, generateRentalDocument, downloadSignBoard, parseOrderFromImage, type Order, type OrderCreateData, type OrderUpdateData } from '@/api/orders'
import { driverApi } from '@/api/drivers'
import { customerApi } from '@/api/customers'
import { leaveApi } from '@/api/leaves'
import { tenantApi } from '@/api'
import type { Driver, AIParsedOrder, Customer } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import SaveResultToast, { type SaveResultToastItem } from '@/components/ui/SaveResultToast'
import DriverCombobox from '@/components/ui/DriverCombobox'
import { cn } from '@/lib/utils'
import {
  isNetworkError, enqueueAction, removeAction, cleanupStaleActions,
  getQueue, getPendingActionsForOrder, cacheOrders, getCachedOrders,
} from '@/utils/offlineQueue'

/** 產生「加入 Google 日曆」連結，跟儀表板首頁同一套邏輯 */
function buildGoogleCalendarUrl(order: Order): string {
  const category = order.order_category
  const marker = category === '送機' ? '送' : category === '接機' ? '接' : category
  const title = `[${marker}] ${order.passenger_name} | ${order.passenger_phone}`

  const start = new Date(order.pickup_datetime.includes('T') ? order.pickup_datetime : order.pickup_datetime.replace(' ', 'T'))
  const end = new Date(start.getTime() + 60 * 60 * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  const fmt = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`

  const stopLines = [...(order.additional_stops || [])]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((s, i) => `中途點${i + 1}：${s.address}${s.name ? `（${s.name}${s.phone ? ' ' + s.phone : ''}）` : ''}`)

  const details = [
    `訂單編號：${order.order_number}`,
    `上車地點：${order.pickup_location}`,
    ...stopLines,
    `下車地點：${order.dropoff_location}`,
    order.flight_number ? `航班：${order.flight_number}` : '',
    `人數：${order.passenger_count}`,
    `行李：${order.luggage_count}`,
    order.child_seat ? `兒童座椅：${order.child_seat}` : '',
    order.booster_seat ? `增高墊：${order.booster_seat}` : '',
    order.name_board ? '需舉牌' : '',
    order.vehicle_type ? `車型：${order.vehicle_type}` : '',
    order.driver_name ? `司機：${order.driver_name}${order.driver_phone ? ' ' + order.driver_phone : ''}` : '',
    order.vehicle_number ? `車牌：${order.vehicle_number}` : '',
    `金額：$${Math.round(order.pricing?.total_amount || 0)}`,
    order.payment_method ? `付款方式：${order.payment_method}` : '',
    order.driver_notes ? `備註：${order.driver_notes}` : '',
  ].filter(Boolean).join('\n')

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmt(start)}/${fmt(end)}`,
    details,
    location: order.pickup_location || '',
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export default function OrdersPage() {
  const [orders, setOrders] = React.useState<Order[]>([])
  const [driverPayConfirmOrder, setDriverPayConfirmOrder] = React.useState<Order | null>(null)
  const [saveToastItems, setSaveToastItems] = React.useState<SaveResultToastItem[]>([])
  const [driverPaySubmitting, setDriverPaySubmitting] = React.useState(false)
  const [drivers, setDrivers] = React.useState<Driver[]>([])
  const [customers, setCustomers] = React.useState<Customer[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('')
  const [categoryFilter, setCategoryFilter] = React.useState('')
  const [dateFilter, setDateFilter] = React.useState<string>('今日')
  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const [customDateFrom, setCustomDateFrom] = React.useState(todayStr)
  const [customDateTo, setCustomDateTo] = React.useState(todayStr)
  const [showCompleted, setShowCompleted] = React.useState(false)
  const [lastOrderMode, setLastOrderMode] = React.useState<'form' | 'ai' | 'image'>(() => {
    return (localStorage.getItem('lastOrderMode') as 'form' | 'ai' | 'image') || 'form'
  })
  const setOrderMode = (mode: 'form' | 'ai' | 'image') => {
    setLastOrderMode(mode)
    localStorage.setItem('lastOrderMode', mode)
  }
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const [selectedOrder, setSelectedOrder] = React.useState<Order | null>(null)
  const [selectedOrders, setSelectedOrders] = React.useState<string[]>([])
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(25)
  // 目前篩選條件在後端實際符合的總筆數（不受前端 searchTerm 二次過濾影響），用來判斷是否還有下一頁
  const [backendTotal, setBackendTotal] = React.useState(0)
  const [sortKey, setSortKey] = React.useState<'date' | 'status' | 'amount'>('date')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc')

  // Get user from auth store
  const { user } = useAuthStore()
  const isDriver = user?.role === 'driver'

  React.useEffect(() => {
    console.log('OrdersPage - user:', user)
    console.log('OrdersPage - isDriver:', isDriver)
    console.log('OrdersPage - user?.role:', user?.role)
  }, [user, isDriver])

  // 司機離線支援：離線快取顯示 + 動作待同步佇列
  const [isShowingOfflineCache, setIsShowingOfflineCache] = React.useState(false)
  const [pendingSyncCount, setPendingSyncCount] = React.useState(0)
  const [isSyncing, setIsSyncing] = React.useState(false)

  // 使用 MutationObserver 監聽 DOM 變化，檢查菜單項
  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      // 檢查是否有新的 menuitem 被添加
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element && node.getAttribute('role') === 'menuitem') {
              console.log('🔍 New menuitem detected:', node.textContent?.trim())
            }
          })
        }
      })

      // 每次 DOM 變化時檢查所有菜單項
      const allMenuItems = document.querySelectorAll('[role="menuitem"]')
      if (allMenuItems.length > 0) {
        const menuTexts = Array.from(allMenuItems).map(el => el.textContent?.trim()).filter(Boolean)
        console.log('📋 Current menu items:', menuTexts)
      }
    })

    // 開始監聽整個文檔的變化
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    })

    return () => observer.disconnect()
  }, [])
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // 檢查 tenant 是否有派車單 / 出租單模板（從 API 取得最新設定，避免 localStorage 過期）
  const parseTenantSettings = (t: any) => {
    const dt = typeof t.document_templates === 'string'
      ? JSON.parse(t.document_templates || '{}')
      : (t.document_templates || {})
    const cfg = typeof t.form_config === 'string'
      ? JSON.parse(t.form_config || '{}')
      : (t.form_config || {})
    return {
      hasDispatch: dt.dispatch !== undefined ? !!dt.dispatch : true,
      hasRental: dt.rental !== undefined ? !!dt.rental : true,
      hideAmount: cfg.hide_dispatch_amount ?? false,
      showDriverFeeInfo: cfg.show_driver_fee_info ?? false,
      extendedCategories: cfg.enable_extended_categories ?? false,
    }
  }

  const [tenantDocTemplates, setTenantDocTemplates] = React.useState(() => {
    try {
      return parseTenantSettings(JSON.parse(localStorage.getItem('tenant') || '{}'))
    } catch { return { hasDispatch: true, hasRental: true, hideAmount: false, showDriverFeeInfo: false, extendedCategories: false } }
  })

  React.useEffect(() => {
    const tenantId = JSON.parse(localStorage.getItem('tenant') || '{}')?.id
    if (!tenantId) return
    tenantApi.get(tenantId).then(t => {
      if (!t || typeof t !== 'object') return
      localStorage.setItem('tenant', JSON.stringify(t))
      setTenantDocTemplates(parseTenantSettings(t))
    }).catch(() => {})
  }, [])

  // AI Order states
  const [isAIDrawerOpen, setIsAIDrawerOpen] = React.useState(false)
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = React.useState(false)
  const [bulkOrders, setBulkOrders] = React.useState<AIParsedOrder[]>([])
  const [aiParsedOrder, setAiParsedOrder] = React.useState<AIParsedOrder | null>(null)
  const [aiRawText, setAiRawText] = React.useState<string>('')

  // Document modal states
  const [isDocumentModalOpen, setIsDocumentModalOpen] = React.useState(false)
  const [documentModalType, setDocumentModalType] = React.useState<'dispatch' | 'rental'>('dispatch')
  const [documentModalOrder, setDocumentModalOrder] = React.useState<Order | null>(null)
  const [documentModalForceAmount, setDocumentModalForceAmount] = React.useState(false)

  // Signature modal states
  const [isSignatureModalOpen, setIsSignatureModalOpen] = React.useState(false)
  const [signatureOrder, setSignatureOrder] = React.useState<Order | null>(null)

  // 司機操作加載狀態
  const [actionLoading, setActionLoading] = React.useState<string | null>(null)
  // 各日期請假司機 map
  const [onLeaveDateMap, setOnLeaveDateMap] = React.useState<Record<string, string[]>>({})
  // 客製簽單待上傳檔案
  const [pendingCustomSignFile, setPendingCustomSignFile] = React.useState<File | null>(null)

  // 訂單文字資訊 modal
  const [textInfoOrder, setTextInfoOrder] = React.useState<Order | null>(null)
  const [textWithDriverInfoOrder, setTextWithDriverInfoOrder] = React.useState<Order | null>(null)
  const [textWithDriverInfoForDriverOrder, setTextWithDriverInfoForDriverOrder] = React.useState<Order | null>(null)
  const [textCopied, setTextCopied] = React.useState(false)
  // 司機資訊 modal
  const [driverInfoModal, setDriverInfoModal] = React.useState<{ order: Order; text: string } | null>(null)

  // 快速新增客戶
  const [isQuickAddCustomerOpen, setIsQuickAddCustomerOpen] = React.useState(false)
  const [quickAddedCustomerId, setQuickAddedCustomerId] = React.useState<string | undefined>(undefined)

  // 圖片辨識建單
  const [imageParseLoading, setImageParseLoading] = React.useState(false)
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | undefined>(undefined)
  const imageInputRef = React.useRef<HTMLInputElement>(null)

  const generateOrderText = (order: Order, opts?: { feeLine?: string; showPaymentMethod?: boolean }): string => {
    const d = (val: any) => (val !== null && val !== undefined && val !== '' && val !== 0 ? String(val) : '-')
    const dt = new Date(order.pickup_datetime)
    const dateStr = `${dt.getFullYear()}/${dt.getMonth() + 1}/${dt.getDate()}`
    const timeStr = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
    const luggageStr = order.luggage_count > 0 ? `${order.luggage_count}大` : '-'
    const specialNeeds = [
      order.child_seat > 0 ? `兒童椅${order.child_seat}個` : '',
      (order.booster_seat ?? 0) > 0 ? `增高墊${order.booster_seat}個` : '',
      order.driver_notes || '',
    ].filter(Boolean).join('、') || '-'

    const extractTerminal = (location: string): string => {
      if (!location.startsWith('桃園機場')) return '-'
      const terminalMap: Record<string, string> = { '一': '第1', '二': '第2', '三': '第3' }
      const match = location.match(/桃園機場(第\d航廈|[一二三]航廈)/)
      if (!match) return '-'
      const t = match[1]
      return terminalMap[t[0]] ? `${terminalMap[t[0]]}航廈` : t
    }

    const nameLine = `姓名：${d(order.passenger_name)}`
    const phoneLine = `電話：${d(order.passenger_phone)}`

    const mapsLine = (location: string): string => {
      if (!location || location === '-') return ''
      return `地圖：https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
    }

    const feeLine = opts?.feeLine ?? (() => {
      const total = order.pricing?.total_amount
      if (!total) return `費用：-`
      const driverFee = order.pricing?.driver_fee
      return `費用：${total}${driverFee ? `/${driverFee}` : ''}`
    })()
    const paymentLine = opts?.showPaymentMethod ? `付款方式：${d(order.payment_method)}` : ''

    const stopsLines = (label: string): string[] => {
      const stops = (order.additional_stops || [])
        .filter(Boolean)
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      return stops
        .map((s, idx) => {
          const parts = [s.name, s.phone, s.address].filter(Boolean)
          if (parts.length === 0) return ''
          return `${label}${idx + 2}：${parts.join(' ')}`
        })
        .filter(Boolean)
    }

    if (order.order_category === '送機') {
      const terminal = extractTerminal(order.dropoff_location)
      return [
        `類別：送機`,
        `日期：${dateStr}`,
        `時間：${timeStr}`,
        `航班：${d(order.flight_number)}`,
        `航廈：${terminal}`,
        nameLine,
        phoneLine,
        `地址：${d(order.pickup_location)}`,
        mapsLine(order.pickup_location),
        ...stopsLines('上車點'),
        `人數：${d(order.passenger_count)}`,
        `行李：${luggageStr}`,
        paymentLine,
        feeLine,
        `特殊需求：${specialNeeds}`,
      ].filter(Boolean).join('\n')
    }

    if (order.order_category === '接機') {
      const terminal = extractTerminal(order.pickup_location)
      return [
        `類別：接機`,
        `日期：${dateStr}`,
        `時間：${timeStr}`,
        `航班：${d(order.flight_number)}`,
        `航廈：${terminal}`,
        nameLine,
        phoneLine,
        `地址：${d(order.dropoff_location)}`,
        mapsLine(order.dropoff_location),
        ...stopsLines('下車點'),
        `人數：${d(order.passenger_count)}`,
        `行李：${luggageStr}`,
        paymentLine,
        feeLine,
        `特殊需求：${specialNeeds}`,
      ].filter(Boolean).join('\n')
    }

    // 其他
    return [
      `類別：其他`,
      `日期：${dateStr}`,
      `時間：${timeStr}`,
      nameLine,
      phoneLine,
      `上車地點：${d(order.pickup_location)}`,
      mapsLine(order.pickup_location),
      `下車地點：${d(order.dropoff_location)}`,
      mapsLine(order.dropoff_location),
      ...stopsLines('停靠點'),
      `人數：${d(order.passenger_count)}`,
      `行李：${luggageStr}`,
      paymentLine,
      feeLine,
      `特殊需求：${specialNeeds}`,
    ].filter(Boolean).join('\n')
  }

  const getDriverVehicleType = (order: Order): string => {
    const driver = drivers.find(d => d.id === order.driver_id)
    if (!driver) return order.external_vehicle_type || order.vehicle_type || '-'
    // 優先比對車號相符的綁定車輛，否則使用司機主要車型
    const allVehicles = [
      { plate: driver.vehicle_plate, type: driver.vehicle_type, brand_model: driver.vehicle_brand_model },
      ...((driver.vehicles || []) as { plate: string; type?: string; brand_model?: string }[]),
    ]
    const matched = allVehicles.find(v => v.plate === order.vehicle_number)
    const type = matched?.type || driver.vehicle_type || order.vehicle_type || '-'
    const brandModel = matched?.brand_model || driver.vehicle_brand_model
    return brandModel ? `${type} / ${brandModel}` : type
  }

  const generateDriverInfoBlock = (order: Order): string => {
    if (!order.driver_name && !order.driver_phone && !order.vehicle_number) return ''
    return `司機：${order.driver_name || '-'}
電話：${order.driver_phone || '-'}
車號：${order.vehicle_number || '-'}
車型：${getDriverVehicleType(order)}`
  }

  const generateOrderWithDriverText = (order: Order): string => {
    const total = order.pricing?.total_amount
    const feeLine = total ? `費用：${total}` : `費用：-`
    const orderText = generateOrderText(order, { feeLine, showPaymentMethod: true })
    const driverBlock = generateDriverInfoBlock(order)

    return driverBlock ? `${orderText}\n\n${driverBlock}` : orderText
  }

  // 訂單+司機資訊(給司機)：現金訂單才顯示「費用/回金」，其他付款方式改顯示「司機費用」而不透露總車資
  const generateOrderWithDriverTextForDriver = (order: Order): string => {
    const total = order.pricing?.total_amount
    const vendorRebate = order.pricing?.vendor_rebate
    const driverFee = order.pricing?.driver_fee
    const isCash = order.payment_method === '現金'
    const feeLine = isCash
      ? (total ? `費用：${total}${vendorRebate ? `/${vendorRebate}` : ''}` : `費用：-`)
      : `司機費用：${driverFee || 0}`
    const orderText = generateOrderText(order, { feeLine, showPaymentMethod: true })
    const driverBlock = generateDriverInfoBlock(order)

    return driverBlock ? `${orderText}\n\n${driverBlock}` : orderText
  }

  const renderNotesWithLinks = (text: string) => {
    if (!text) return null

    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = text.split(urlRegex)

    return (
      <span>
        {parts.map((part, index) => {
          if (urlRegex.test(part)) {
            return (
              <a
                key={index}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                {part}
              </a>
            )
          }
          return part
        })}
      </span>
    )
  }

  const handleCopyText = async (text: string, onClose?: () => void) => {
    await navigator.clipboard.writeText(text)
    setTextCopied(true)
    setTimeout(() => {
      setTextCopied(false)
      onClose?.()
    }, 1000)
  }


  const fetchDrivers = async () => {
    try {
      const result = await driverApi.getDrivers(1, 100)
      setDrivers(result.items || [])
    } catch (error) {
      console.error('Failed to fetch drivers:', error)
    }
  }

  const fetchCustomers = async () => {
    try {
      const result = await customerApi.getCustomers(1, 100)
      setCustomers(result.items || [])
    } catch (error) {
      console.error('Failed to fetch customers:', error)
    }
  }

  const handleQuickAddCustomer = async (data: Parameters<typeof customerApi.createCustomer>[0]) => {
    try {
      const newCustomer = await customerApi.createCustomer(data)
      setCustomers((prev) => [...prev, newCustomer])
      setQuickAddedCustomerId(newCustomer.id)
      // Reset auto-select flag after OrderDrawer picks it up
      setTimeout(() => setQuickAddedCustomerId(undefined), 500)
    } catch (error: any) {
      alert(error?.response?.data?.detail || '新增客戶失敗，請稍後再試')
      throw error
    }
  }

  // 分頁頁碼列：目前頁前後各留 1 頁，頭尾一定顯示，中間省略的地方用 '…' 表示
  const getPageNumbers = (current: number, totalPages: number): (number | '…')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages = new Set<number>([1, totalPages, current, current - 1, current + 1])
    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b)
    const result: (number | '…')[] = []
    sorted.forEach((p, idx) => {
      if (idx > 0 && p - sorted[idx - 1] > 1) result.push('…')
      result.push(p)
    })
    return result
  }

  // 日期篩選前一日/後一日：以目前的開始日期為準位移，若有結束日期則保持區間天數不變
  const shiftCustomDate = (direction: 1 | -1) => {
    if (!customDateFrom) return
    const from = new Date(`${customDateFrom}T00:00:00`)
    const to = customDateTo ? new Date(`${customDateTo}T00:00:00`) : from
    const rangeDays = Math.round((to.getTime() - from.getTime()) / 86400000)
    const newFrom = addDays(from, direction)
    const newTo = addDays(newFrom, rangeDays)
    setCustomDateFrom(format(newFrom, 'yyyy-MM-dd'))
    setCustomDateTo(format(newTo, 'yyyy-MM-dd'))
    setDateFilter('')
    setPage(1)
  }

  const fetchOrders = async () => {
    setIsLoading(true)
    try {
      const today = startOfToday()
      const tomorrow = startOfTomorrow()
      const tomorrowEnd = endOfTomorrow()
      const dayAfterTomorrow = addDays(today, 2)
      const dayAfterTomorrowEnd = addDays(today, 3)
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
      const weekEnd = endOfWeek(new Date())

      let date_from: string | undefined
      let date_to: string | undefined
      if (customDateFrom || customDateTo) {
        date_from = customDateFrom || undefined
        date_to = customDateTo || undefined
      } else if (dateFilter === '今日') {
        date_from = format(today, 'yyyy-MM-dd')
        date_to = format(today, 'yyyy-MM-dd')
      } else if (dateFilter === '明日') {
        date_from = format(tomorrow, 'yyyy-MM-dd')
        date_to = format(tomorrow, 'yyyy-MM-dd')
      } else if (dateFilter === '後天') {
        date_from = format(dayAfterTomorrow, 'yyyy-MM-dd')
        date_to = format(dayAfterTomorrow, 'yyyy-MM-dd')
      } else if (dateFilter === '本周') {
        date_from = format(weekStart, 'yyyy-MM-dd')
        date_to = format(weekEnd, 'yyyy-MM-dd')
      }

      const result = await getOrders({
        page,
        limit: pageSize,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        date_from,
        date_to,
      })

      setBackendTotal(result.total ?? 0)

      let filteredData = result.data || []

      if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase()
        // 日期搜尋容錯：同時比對補零/不補零、"/" 與 "-" 分隔的各種寫法（07-01、7-1、07/01、7/1...）
        const normalizeDateStr = (s: string) => s.replace(/-/g, '/').replace(/\b0+(\d)/g, '$1')
        const normalizedSearch = normalizeDateStr(lowerSearch)
        filteredData = filteredData.filter(o => {
          const pickupDate = new Date(o.pickup_datetime)
          const dateVariants = [
            format(pickupDate, 'yyyy/MM/dd HH:mm'),
            format(pickupDate, 'yyyy/M/d'),
            format(pickupDate, 'M/d'),
            format(pickupDate, 'MM/dd'),
          ]
          const matchesDate = dateVariants.some(v => normalizeDateStr(v.toLowerCase()).includes(normalizedSearch))
          return (
            o.order_number.toLowerCase().includes(lowerSearch) ||
            o.passenger_name.toLowerCase().includes(lowerSearch) ||
            o.passenger_phone.includes(lowerSearch) ||
            o.pickup_location.toLowerCase().includes(lowerSearch) ||
            o.dropoff_location.toLowerCase().includes(lowerSearch) ||
            o.vehicle_type.toLowerCase().includes(lowerSearch) ||
            (o.driver_name || '').toLowerCase().includes(lowerSearch) ||
            (o.customer_name || '').toLowerCase().includes(lowerSearch) ||
            matchesDate
          )
        })
      }

      setOrders(filteredData)
      setTotal(filteredData.length)
      setIsShowingOfflineCache(false)
      if (isDriver) cacheOrders(dateFilter, filteredData)

      // 抓取所有訂單日期的請假司機
      const uniqueDates = [...new Set(
        filteredData
          .filter(o => o.pickup_datetime)
          .map(o => format(new Date(o.pickup_datetime), 'yyyy-MM-dd'))
      )]
      if (uniqueDates.length > 0) {
        Promise.all(
          uniqueDates.map(date =>
            leaveApi.getOnDateDriverIds(date)
              .then(ids => ({ date, ids }))
              .catch(() => ({ date, ids: [] as string[] }))
          )
        ).then(results => {
          const map: Record<string, string[]> = {}
          results.forEach(({ date, ids }) => { map[date] = ids })
          setOnLeaveDateMap(map)
        })
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error)
      if (isDriver && isNetworkError(error)) {
        const cached = getCachedOrders(dateFilter)
        if (cached) {
          setOrders(cached.orders)
          setTotal(cached.orders.length)
          setIsShowingOfflineCache(true)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 把離線期間排隊的司機動作依序送出；只要有一筆失敗（通常是還沒恢復網路）就停止，保留順序下次繼續
  const trySyncOfflineQueue = React.useCallback(async () => {
    if (!isDriver) return
    cleanupStaleActions()
    const queue = getQueue()
    setPendingSyncCount(queue.length)
    if (queue.length === 0 || isSyncing) return

    setIsSyncing(true)
    try {
      for (const item of getQueue()) {
        try {
          if (item.action === '簽名') {
            await uploadSignature(item.order_id, item.payload.signatureDataUrl)
            await updateOrder(item.order_id, { is_signed: true, signed_at: new Date().toISOString() })
          } else {
            await updateOrder(item.order_id, item.payload as OrderUpdateData)
          }
          removeAction(item.id)
        } catch (error) {
          if (isNetworkError(error)) break // 還是離線，之後再試
          removeAction(item.id) // 伺服器明確拒絕（例如單已不存在），丟掉避免卡住佇列
        }
      }
    } finally {
      setPendingSyncCount(getQueue().length)
      setIsSyncing(false)
      fetchOrders()
    }
  }, [isDriver, isSyncing])

  React.useEffect(() => {
    if (!isDriver) return
    trySyncOfflineQueue()
    window.addEventListener('online', trySyncOfflineQueue)
    return () => window.removeEventListener('online', trySyncOfflineQueue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriver])

  React.useEffect(() => {
    fetchDrivers()
    fetchCustomers()
  }, [])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrders()
    }, 150)
    return () => clearTimeout(timer)
  }, [page, pageSize, statusFilter, categoryFilter, dateFilter, customDateFrom, customDateTo, searchTerm])

  // 從儀表板日曆點擊跳入：自動開啟指定訂單的抽屜
  React.useEffect(() => {
    const editOrderId = searchParams.get('editOrder')
    if (!editOrderId) return
    const openDrawer = async () => {
      try {
        const order = await getOrder(editOrderId)
        setSelectedOrder(order)
        setIsDrawerOpen(true)
      } catch {
        // 找不到就忽略
      } finally {
        // 清掉 URL 參數避免重整再開
        setSearchParams(prev => { prev.delete('editOrder'); return prev }, { replace: true })
      }
    }
    openDrawer()
  }, [searchParams])

  // 每 5 分鐘自動刷新訂單列表
  React.useEffect(() => {
    const pollingInterval = setInterval(() => {
      fetchOrders()
    }, 5 * 60 * 1000) // 5 分鐘

    return () => clearInterval(pollingInterval)
  }, [page, pageSize, statusFilter, categoryFilter, dateFilter, customDateFrom, customDateTo, searchTerm])

  const handleCreate = () => {
    setSelectedOrder(null)
    setIsDrawerOpen(true)
  }

  const handleImageParse = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // 建立圖片預覽 URL（先清除舊的）
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl)
    const previewUrl = URL.createObjectURL(file)
    setImagePreviewUrl(previewUrl)
    e.target.value = ''
    setImageParseLoading(true)
    try {
      const result = await parseOrderFromImage(file)
      if (!result.success || !result.data?.orders?.length) {
        alert(result.error || '無法辨識訂單，請換張圖片試試')
        URL.revokeObjectURL(previewUrl)
        setImagePreviewUrl(undefined)
        return
      }
      const o = result.data.orders[0] as any
      // 解析日期時間
      let pickupDate: string | undefined
      if (o.pickup_datetime) {
        try {
          const d = new Date(o.pickup_datetime)
          pickupDate = d.toISOString()
        } catch {}
      }
      const parsed: AIParsedOrder = {
        order_category: o.order_category || '其他',
        passenger_name: o.passenger_name || '貴賓',
        passenger_phone: o.passenger_phone || '',
        passenger_count: o.passenger_count || 1,
        pickup_datetime: pickupDate || new Date().toISOString(),
        pickup_location: o.pickup_location || '',
        dropoff_location: o.dropoff_location || '',
        additional_stops: o.additional_stops,
        is_round_trip: o.is_round_trip || false,
        flight_number: o.flight_number,
        vehicle_type: o.vehicle_type || '',
        luggage_count: o.luggage_count || 0,
        child_seat: o.child_seat || 0,
        booster_seat: o.booster_seat || 0,
        name_board: o.name_board || false,
        pricing: o.pricing || { total_amount: 0, driver_amount: 0, base_fare: 0, night_surcharge: 0, cross_district_fee: 0, driver_fee: 0, vendor_rebate: 0, company_rebate: 0 },
        payment_method: o.payment_method || '現金',
        driver_notes: o.driver_notes,
      }
      setAiParsedOrder(parsed)
      setSelectedOrder(null)
      setIsDrawerOpen(true)
    } catch {
      alert('圖片辨識失敗，請稍後再試')
    } finally {
      setImageParseLoading(false)
    }
  }

  const handleEdit = (order: Order) => {
    setSelectedOrder(order)
    setIsDrawerOpen(true)
  }

  // AI Order handlers
  const handleAISingleOrder = (order: AIParsedOrder, rawText: string) => {
    // Convert AI parsed order to OrderCreateData format for createOrder API
    const orderForCreate: OrderCreateData = {
      order_category: order.order_category,
      passenger_name: order.passenger_name,
      passenger_phone: order.passenger_phone || '',
      passenger_count: order.passenger_count,
      pickup_datetime: order.pickup_datetime,
      pickup_location: order.pickup_location,
      dropoff_location: order.dropoff_location,
      additional_stops: order.additional_stops,
      is_round_trip: order.is_round_trip,
      flight_number: order.flight_number,
      vehicle_type: order.vehicle_type,
      luggage_count: order.luggage_count,
      child_seat: order.child_seat,
      name_board: order.name_board,
      is_signed: false,
      pricing: order.pricing,
      payment_method: order.payment_method,
      driver_notes: order.driver_notes,
      customer_id: order.customer_id,
    }

    // Create the order directly
    // Open OrderDrawer for user to review before creating
    const orderForDrawer = {
      id: '',
      order_number: '',
      tenant_id: '',
      order_category: order.order_category,
      status: '待派車',
      passenger_name: order.passenger_name,
      passenger_phone: order.passenger_phone || '',
      passenger_count: order.passenger_count,
      pickup_datetime: order.pickup_datetime,
      pickup_location: order.pickup_location || '',
      pickup_airport: (order as any).pickup_airport,
      pickup_terminal: (order as any).pickup_terminal,
      dropoff_location: order.dropoff_location || '',
      dropoff_airport: (order as any).dropoff_airport,
      dropoff_terminal: (order as any).dropoff_terminal,
      additional_stops: order.additional_stops,
      is_round_trip: order.is_round_trip || false,
      flight_number: order.flight_number || '',
      vehicle_type: order.vehicle_type,
      luggage_count: order.luggage_count,
      child_seat: order.child_seat || false,
      name_board: order.name_board || false,
      pricing: order.pricing || {
        total_amount: 0,
        driver_amount: 0,
        base_fare: 0,
        night_surcharge: 0,
        cross_district_fee: 0,
      },
      payment_method: order.payment_method || '現金',
      driver_notes: order.driver_notes || '',
      customer_id: order.customer_id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setSelectedOrder(orderForDrawer as any)
    setAiParsedOrder(null)
    setAiRawText(rawText)
    setIsDrawerOpen(true)
  }

  const handleAIMultipleOrders = (orders: AIParsedOrder[], rawText: string) => {
    setBulkOrders(orders)
    setAiRawText(rawText)
    setIsBulkDrawerOpen(true)
  }

  const handleAIEditOrder = (order: AIParsedOrder, index: number) => {
    // Update the specific order in bulkOrders list
    const updatedOrders = [...bulkOrders]
    updatedOrders[index] = order
    setBulkOrders(updatedOrders)

    // Open OrderDrawer for editing (with index to track which one)
    const orderForDrawer = {
      id: '',
      order_number: '',
      tenant_id: '',
      order_category: order.order_category,
      status: '待派車',
      passenger_name: order.passenger_name,
      passenger_phone: order.passenger_phone || '',
      passenger_count: order.passenger_count,
      pickup_datetime: order.pickup_datetime,
      pickup_location: order.pickup_location || '',
      pickup_airport: (order as any).pickup_airport,
      pickup_terminal: (order as any).pickup_terminal,
      dropoff_location: order.dropoff_location || '',
      dropoff_airport: (order as any).dropoff_airport,
      dropoff_terminal: (order as any).dropoff_terminal,
      additional_stops: order.additional_stops,
      is_round_trip: order.is_round_trip || false,
      flight_number: order.flight_number || '',
      vehicle_type: order.vehicle_type,
      luggage_count: order.luggage_count,
      child_seat: order.child_seat || false,
      name_board: order.name_board || false,
      pricing: order.pricing || {
        total_amount: 0,
        driver_amount: 0,
        base_fare: 0,
        night_surcharge: 0,
        cross_district_fee: 0,
      },
      payment_method: order.payment_method || '現金',
      driver_notes: order.driver_notes || '',
      customer_id: order.customer_id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _editIndex: index,  // Track which order is being edited
    }
    setSelectedOrder(orderForDrawer as any)
    setAiParsedOrder(null)
    setIsBulkDrawerOpen(false)
    setIsDrawerOpen(true)
  }

  const handleBulkSuccess = () => {
    setIsBulkDrawerOpen(false)
    setBulkOrders([])
    fetchOrders()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此訂單嗎？')) return
    try {
      await deleteOrder(id)
      fetchOrders()
    } catch (error) {
      console.error('Failed to delete order:', error)
    }
  }

  const handleToggleSelectAll = () => {
    if (selectedOrders.length === orders.length && orders.length > 0) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(orders.map(o => o.id))
    }
  }

  const handleToggleSelectOne = (id: string) => {
    if (selectedOrders.includes(id)) {
      setSelectedOrders(selectedOrders.filter(o => o !== id))
    } else {
      setSelectedOrders([...selectedOrders, id])
    }
  }

  const handleBulkDelete = async () => {
    if (!confirm(`確定要刪除選取的 ${selectedOrders.length} 筆訂單嗎？`)) return
    try {
      // Assuming a bulk delete API exists or iterating delete calls
      // For now, iterate:
      await Promise.all(selectedOrders.map(id => deleteOrder(id)))
      setSelectedOrders([])
      fetchOrders()
    } catch (error) {
      console.error('Failed to delete orders:', error)
    }
  }

  const handleDriverChange = async (orderId: string, driverId: string) => {
    try {
      const driver = drivers.find(d => d.id === driverId)
      const updateData: OrderUpdateData = {
        driver_id: driverId || null,
        driver_name: driver?.name || null,
        driver_phone: driver?.phone || null,
        vehicle_number: driver?.vehicle_plate || null,
        status: driverId ? '已派車' : '待派車',
      }
      await updateOrder(orderId, updateData)
      fetchOrders()
    } catch (error) {
      console.error('Failed to assign driver:', error)
    }
  }

  // 標記「已付款給司機」——跟客戶的付款方式（payment_method）無關，是車行付給司機的司機費用
  // 點按鈕先開確認視窗（見 driverPayConfirmOrder），使用者按確認才真的送出
  const handleMarkDriverPaid = (order: Order) => {
    setDriverPayConfirmOrder(order)
  }

  const handleConfirmDriverPaid = async () => {
    const order = driverPayConfirmOrder
    if (!order) return
    setDriverPaySubmitting(true)
    try {
      const updated = await updateOrder(order.id, { driver_paid_at: new Date().toISOString() })
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, driver_paid_at: updated.driver_paid_at } : o))
      setDriverPayConfirmOrder(null)
    } catch (error) {
      console.error('Failed to mark driver paid:', error)
    } finally {
      setDriverPaySubmitting(false)
    }
  }

  // 將 UTC naive 時間字串（無 Z）解析為本地時間顯示
  const fmtTime = (ts: string) => {
    const d = ts.endsWith('Z') || ts.includes('+') ? new Date(ts) : new Date(ts + 'Z')
    return format(d, 'HH:mm')
  }

  // 司機操作按鈕 handlers
  const handleDriverAction = async (order: Order, action: '出發' | '客上' | '客下') => {
    const now = new Date().toISOString()
    let newStatus = order.status
    let updateData: OrderUpdateData = { status: newStatus }
    try {
      if (action === '出發') {
        newStatus = '進行中'
        updateData = { 
          status: newStatus,
          departed_at: now 
        }
      } else if (action === '客上') {
        newStatus = '進行中'
        updateData = { 
          status: newStatus,
          picked_up_at: now 
        }
      } else if (action === '客下') {
        newStatus = '已完成'
        updateData = { 
          status: newStatus,
          completed_at: now 
        }
      }
      
      await updateOrder(order.id, updateData)
      fetchOrders()
    } catch (error) {
      console.error(`Failed to ${action}:`, error)
      if (isNetworkError(error)) {
        // 離線：樂觀更新畫面，動作排進待同步佇列，恢復網路後自動送出
        enqueueAction(order.id, action, updateData)
        setPendingSyncCount(getQueue().length)
        setOrders(prev => prev.map(o => o.id === order.id ? { ...o, ...updateData } as Order : o))
      }
    }
  }

  const handleViewSignature = (order: Order) => {
    // 司機點擊簽名，打開簽名 modal
    setActionLoading(order.id)
    setSignatureOrder(order)
    setIsSignatureModalOpen(true)
  }

  const handleSaveSignature = async (signatureDataUrl: string) => {
    if (!signatureOrder) return

    try {
      await uploadSignature(signatureOrder.id, signatureDataUrl)
      await updateOrder(signatureOrder.id, {
        is_signed: true,
        signed_at: new Date().toISOString()
      })
      // 更新本地 state 讓按鈕立即 disabled
      setOrders(prev => prev.map(o =>
        o.id === signatureOrder.id
          ? { ...o, signature_image_path: 'signed', is_signed: true }
          : o
      ))
      setSignatureOrder(null)
      setActionLoading(null)
      setIsSignatureModalOpen(false)
    } catch (error) {
      console.error('Failed to save signature:', error)
      if (isNetworkError(error)) {
        // 離線：把簽名圖檔存進待同步佇列，畫面照樣視為已簽名
        enqueueAction(signatureOrder.id, '簽名', { signatureDataUrl })
        setPendingSyncCount(getQueue().length)
        setOrders(prev => prev.map(o =>
          o.id === signatureOrder.id
            ? { ...o, signature_image_path: 'signed', is_signed: true }
            : o
        ))
        setSignatureOrder(null)
        setActionLoading(null)
        setIsSignatureModalOpen(false)
        return
      }
      setActionLoading(null)
      throw error
    }
  }

  const handleSubmit = async (data: OrderCreateData | OrderUpdateData) => {
    try {
      // Check if it's an existing order (has valid id) or new order
      const isExistingOrder = selectedOrder?.id && selectedOrder.id !== ''
      const isFromBulkEdit = (selectedOrder as any)?._editIndex !== undefined
      let savedOrder: Order | undefined

      if (isExistingOrder) {
        savedOrder = await updateOrder(selectedOrder.id, data as OrderUpdateData)
        // 編輯時也要上傳客製簽單底圖（新上傳或更換）
        if (pendingCustomSignFile) {
          try {
            await uploadCustomSignImage(selectedOrder.id, pendingCustomSignFile)
          } catch (e) {
            console.error('客製簽單底圖上傳失敗', e)
          }
          setPendingCustomSignFile(null)
        }
      } else {
        const newOrder = await createOrder(data as OrderCreateData)
        savedOrder = newOrder
        // 上傳客製簽單底圖
        if (pendingCustomSignFile) {
          try {
            await uploadCustomSignImage(newOrder.id, pendingCustomSignFile)
          } catch (e) {
            console.error('客製簽單底圖上傳失敗', e)
          }
          setPendingCustomSignFile(null)
        }
        // If editing from bulk list, update the specific order
        if (isFromBulkEdit && (selectedOrder as any)?._editIndex !== undefined) {
          const editIndex = (selectedOrder as any)._editIndex
          const updatedBulkOrders = [...bulkOrders]
          updatedBulkOrders[editIndex] = {
            ...data,
            pickup_datetime: typeof data.pickup_datetime === 'string'
              ? data.pickup_datetime
              : new Date(data.pickup_datetime).toISOString(),
          } as AIParsedOrder
          setBulkOrders(updatedBulkOrders)
        }
      }
      // Clear AI parsed order after successful submission
      setAiParsedOrder(null)
      setSelectedOrder(null)  // Clear selected order after submit
      fetchOrders()
      return savedOrder
    } catch (error) {
      console.error('Failed to save order:', error)
      throw error
    }
  }

  const handleOrderSaved = (items: { order: Order; label: string }[]) => {
    setSaveToastItems(items.map(({ order, label }) => ({
      id: order.id,
      order_number: order.order_number,
      pickup_datetime: order.pickup_datetime,
      label,
    })))
  }

  const handleOpenSavedOrder = (id: string) => {
    const order = orders.find(o => o.id === id)
    if (order) {
      handleEdit(order)
    } else {
      // 剛儲存的訂單可能不在目前篩選範圍內，直接用最少資訊打開編輯畫面
      getOrder(id).then(handleEdit).catch(() => alert('找不到該筆訂單，可能已被刪除'))
    }
    setSaveToastItems([])
  }

  const handleSaveAsNew = async (data: OrderCreateData) => {
    try {
      await createOrder(data)
      setSelectedOrder(null)
      fetchOrders()
    } catch (error) {
      console.error('Failed to save as new order:', error)
      throw error
    }
  }

  const handleCopyDriverInfo = (order: Order) => {
    if (!order.driver_name) {
      alert('此訂單尚未指派司機')
      return
    }

    const text = generateDriverInfoBlock(order)
    setDriverInfoModal({ order, text })
  }

  const handleCopyExternalSignLink = async (order: Order) => {
    const driver = drivers.find(d => d.id === order.driver_id)
    if (!driver?.sign_token) {
      alert('找不到該司機的簽名連結')
      return
    }
    const url = `${window.location.origin}/driver-sign/${driver.sign_token}`
    await navigator.clipboard.writeText(url)
    alert('已複製外派簽名連結，貼給司機開啟即可看到這筆待簽名訂單')
  }

  const handleCopyOrderSignLink = async (order: Order) => {
    try {
      const token = await createExternalSignLink(order.id)
      await navigator.clipboard.writeText(`${window.location.origin}/ext-sign/${token}`)
      alert('已複製外派簽單連結。貼給外派司機，司機自行填寫資料後讓客戶簽名；簽完訂單會變成已完成，連結即失效。')
    } catch (e: any) {
      alert(e?.response?.data?.detail || '產生連結失敗，請稍後再試')
    }
  }

  const handleViewDispatchDocument = async (order: Order, forceAmount = false) => {
    // 如果沒有派車單，先生成（客戶版一律即時產生最新金額，不用這個快取判斷）
    if (!forceAmount && !order.dispatch_document_path) {
      try {
        await generateDispatchDocument(order.id)
        // 重新取得訂單資料以獲取最新路徑
        fetchOrders()
      } catch (error) {
        console.error('Failed to generate dispatch document:', error)
        alert('無法生成派車單，請稍後再試')
        return
      }
    }
    setDocumentModalOrder(order)
    setDocumentModalType('dispatch')
    setDocumentModalForceAmount(forceAmount)
    setIsDocumentModalOpen(true)
  }

  const handleViewRentalDocument = async (order: Order) => {
    // 如果沒有出租單，先生成
    if (!order.rental_document_path) {
      try {
        await generateRentalDocument(order.id)
        // 重新取得訂單資料以獲取最新路徑
        fetchOrders()
      } catch (error) {
        console.error('Failed to generate rental document:', error)
        alert('無法生成出租單，請稍後再試')
        return
      }
    }
    setDocumentModalOrder(order)
    setDocumentModalType('rental')
    setIsDocumentModalOpen(true)
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { text: string; className: string }> = {
      '待派車': { text: '待派車', className: 'bg-pink-200 text-pink-800 dark:bg-pink-800 dark:text-pink-200' },
      '已派車': { text: '已派車', className: 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200' },
      '進行中': { text: '進行中', className: 'bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200' },
      '已完成': { text: '已完成', className: 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' },
      '已取消': { text: '已取消', className: 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' },
    }
    const config = statusMap[status] || { text: status, className: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300' }
    return (
      <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap', config.className)}>
        {config.text}
      </span>
    )
  }

  const PAYMENT_COLOR_PALETTE = [
    'bg-emerald-500', 'bg-purple-500', 'bg-sky-500', 'bg-orange-500',
    'bg-pink-500', 'bg-teal-500', 'bg-amber-500', 'bg-indigo-500',
    'bg-rose-500', 'bg-lime-600', 'bg-cyan-500', 'bg-fuchsia-500',
  ]

  const getPaymentBadge = (method: string) => {
    const map: Record<string, string> = {
      '現金': 'bg-emerald-500 text-white',
      '刷卡': 'bg-purple-500 text-white',
      '轉帳': 'bg-sky-500 text-white',
      '月結': 'bg-orange-500 text-white',
    }
    let cls = map[method]
    if (!cls) {
      // 未預設顏色的自訂付款方式，依名稱雜湊分配固定顏色
      let hash = 0
      for (let i = 0; i < method.length; i++) hash = (hash * 31 + method.charCodeAt(i)) >>> 0
      cls = `${PAYMENT_COLOR_PALETTE[hash % PAYMENT_COLOR_PALETTE.length]} text-white`
    }
    return (
      <span className={cn('px-2.5 py-1 rounded-full text-sm font-semibold whitespace-nowrap', cls)}>
        {method}
      </span>
    )
  }

  const getCategoryBadge = (category: string) => {
    const categoryMap: Record<string, string> = {
      '送機': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700',
      '接機': 'bg-orange-500 dark:bg-orange-600 text-white border border-orange-600 dark:border-orange-500 font-bold shadow-sm',
      '其他': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700',
    }
    return (
      <span className={cn('inline-block shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-semibold', categoryMap[category] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700')}>
        {category}
      </span>
    )
  }

  const getRowStyles = (status: string, isSelected: boolean) => {
    const statusColorMap: Record<string, string> = {
      '待派車': 'bg-pink-100/50 dark:bg-pink-900/20',
      '已派車': 'bg-blue-100/50 dark:bg-blue-900/20',
      '進行中': 'bg-amber-100/50 dark:bg-amber-900/20',
      '已完成': 'bg-green-100/50 dark:bg-green-900/20',
      '已取消': 'bg-red-100/50 dark:bg-red-900/20',
    }
    const baseClass = statusColorMap[status] || 'bg-white dark:bg-gray-800'
    return cn(
      baseClass,
      isSelected && 'ring-2 ring-inset ring-blue-500 dark:ring-blue-400'
    )
  }

  const STATUS_ORDER: Record<string, number> = { '待派車': 0, '已派車': 1, '進行中': 2, '已完成': 3, '已取消': 4 }

  const handleSort = (key: 'date' | 'status' | 'amount') => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortedOrders = React.useMemo(() => {
    return [...orders].sort((a, b) => {
      let cmp = 0
      if (sortKey === 'date') {
        cmp = (a.pickup_datetime || '').localeCompare(b.pickup_datetime || '')
      } else if (sortKey === 'status') {
        cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9)
      } else if (sortKey === 'amount') {
        cmp = (a.pricing?.total_amount || 0) - (b.pricing?.total_amount || 0)
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [orders, sortKey, sortDir])

  const SortIcon = ({ k }: { k: 'date' | 'status' | 'amount' }) => {
    if (sortKey !== k) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-40" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-blue-500" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-blue-500" />
  }

  return (
    <div className="p-1.5 sm:p-6 pb-56 sm:pb-56">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">派車單管理</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchOrders}
            className="h-9 px-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <RefreshCw className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {isDriver && (isShowingOfflineCache || pendingSyncCount > 0) && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          <WifiOff className="h-4 w-4 shrink-0" />
          <span>
            {isShowingOfflineCache && '目前離線，顯示上次連線的資料。'}
            {pendingSyncCount > 0 && `${isSyncing ? '同步中…' : '有'} ${pendingSyncCount} 筆動作待同步`}
          </span>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex flex-wrap items-center gap-2">
            {!isDriver && (
              <>
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="搜尋編號、乘客..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-9 py-1.5 h-9 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white outline-none"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value)
                    setPage(1)
                  }}
                  className="h-9 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="">全部類別</option>
                  <option value="送機">送機</option>
                  <option value="接機">接機</option>
                  {tenantDocTemplates.extendedCategories ? (
                    <>
                      <option value="單程">單程</option>
                      <option value="包車">包車</option>
                    </>
                  ) : (
                    <option value="其他">其他</option>
                  )}
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setPage(1)
                  }}
                  className="h-9 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="">全部狀態</option>
                  <option value="待派車">待派車</option>
                  <option value="已派車">已派車</option>
                  <option value="進行中">進行中</option>
                  <option value="已完成">已完成</option>
                  <option value="已取消">已取消</option>
                </select>
              </>
            )}

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant={dateFilter === '今日' && customDateFrom === todayStr && customDateTo === todayStr ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setDateFilter('今日')
                  setCustomDateFrom(todayStr)
                  setCustomDateTo(todayStr)
                  setPage(1)
                }}
                className={cn(
                  "h-9 px-3 text-xs font-bold transition-all shrink-0",
                  dateFilter === '今日' && customDateFrom === todayStr && customDateTo === todayStr
                    ? "shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                )}
              >
                今日
              </Button>
              <Button
                type="button"
                variant={!customDateFrom && !customDateTo && dateFilter === '全部' ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setDateFilter('全部')
                  setCustomDateFrom('')
                  setCustomDateTo('')
                  setPage(1)
                }}
                className={cn(
                  "h-9 px-3 text-xs font-bold transition-all shrink-0",
                  !customDateFrom && !customDateTo && dateFilter === '全部'
                    ? "shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                )}
              >
                全部
              </Button>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={!customDateFrom}
                  onClick={() => shiftCustomDate(-1)}
                  title="前一日"
                  className="h-9 w-8 flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 hover:text-gray-800 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="relative">
                  <input
                    type="date"
                    value={customDateFrom}
                    max={customDateTo || undefined}
                    onChange={(e) => {
                      const newFrom = e.target.value
                      setCustomDateFrom(newFrom)
                      setDateFilter('')
                      // 結束日期還沒填，或比新的開始日期還早時，預設帶成同一天，方便查單日
                      if (newFrom && (!customDateTo || customDateTo < newFrom)) {
                        setCustomDateTo(newFrom)
                      }
                      setPage(1)
                    }}
                    className={cn(
                      "h-9 pl-2 pr-8 text-sm border rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer",
                      customDateFrom ? "border-blue-400" : "border-gray-300 dark:border-gray-600"
                    )}
                  />
                  {customDateFrom && (
                    <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-full text-[10px] font-black min-w-[17px] h-[17px] px-1 bg-blue-600 text-white shadow-sm ring-1 ring-white dark:ring-gray-800">
                      {searchTerm ? total : backendTotal}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={!customDateFrom}
                  onClick={() => shiftCustomDate(1)}
                  title="後一日"
                  className="h-9 w-8 flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 hover:text-gray-800 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="hidden lg:flex items-center gap-1">
                <span className="text-gray-400 text-sm">至</span>
                <input
                  type="date"
                  value={customDateTo}
                  min={customDateFrom || undefined}
                  onChange={(e) => {
                    setCustomDateTo(e.target.value)
                    setDateFilter('')
                    setPage(1)
                  }}
                  className={cn(
                    "h-9 px-2 text-sm border rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer",
                    customDateTo ? "border-blue-400" : "border-gray-300 dark:border-gray-600"
                  )}
                />
                {(customDateFrom || customDateTo) && (
                  <button
                    type="button"
                    onClick={() => { setCustomDateFrom(''); setCustomDateTo(''); setDateFilter('全部'); setPage(1) }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                    title="清除日期篩選"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {isDriver && (
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setPage(1)
                  }}
                  className="h-9 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="">全部狀態</option>
                  <option value="待派車">待派車</option>
                  <option value="已派車">已派車</option>
                  <option value="進行中">進行中</option>
                  <option value="已完成">已完成</option>
                  <option value="已取消">已取消</option>
                </select>
              )}
              {isDriver && (
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <Checkbox
                    checked={showCompleted}
                    onCheckedChange={(v) => setShowCompleted(!!v)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">顯示已完成</span>
                </label>
              )}
              <select
                value={`${sortKey}-${sortDir}`}
                onChange={(e) => {
                  const [k, d] = e.target.value.split('-') as ['date' | 'status' | 'amount', 'asc' | 'desc']
                  setSortKey(k)
                  setSortDir(d)
                }}
                className="md:hidden h-9 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
              >
                <option value="date-asc">日期 ↑</option>
                <option value="date-desc">日期 ↓</option>
                <option value="status-asc">狀態 ↑</option>
                <option value="status-desc">狀態 ↓</option>
                <option value="amount-asc">金額 ↑</option>
                <option value="amount-desc">金額 ↓</option>
              </select>
            </div>
          </div>
        </div>

        {!isDriver && selectedOrders.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 flex items-center justify-between border-b border-blue-100 dark:border-blue-800">
            <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
              已選取 {selectedOrders.length} 筆訂單
            </span>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={handleBulkDelete}
                className="h-8 text-xs"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                刪除選取項目
              </Button>
            </div>
          </div>
        )}

        {/* Mobile Card View */}
        <div className="md:hidden p-2 space-y-3 bg-gray-200/70 dark:bg-gray-950/40">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">正在加載訂單...</div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-gray-500">目前沒有訂單資料</div>
          ) : (
            sortedOrders
              .filter(order => !isDriver || showCompleted || !order.status.includes('完成'))
              .map((order) => {
              const isSelected = selectedOrders.includes(order.id)
              const isCompleted = order.status.includes('完成')
              return (
                <div
                  key={order.id}
                  className={cn(
                    "p-3 space-y-2 transition-all duration-200 rounded-xl border border-gray-300 dark:border-gray-600 shadow-md overflow-hidden",
                    getRowStyles(order.status, isSelected)
                  )}
                >
                  {/* 第1行：左邊(日期 + 分類 + 訂單號) + 右邊(金額 + 狀態) */}
                  <div className="flex items-start justify-between gap-2">
                    {/* 左邊 */}
                    <div className="flex items-center gap-3">
                      {!isDriver && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleSelectOne(order.id)}
                        />
                      )}
                      <div>
                        <div className="text-base font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(buildGoogleCalendarUrl(order), '_blank', 'noopener,noreferrer')
                            }}
                            title="加入 Google 日曆"
                            className="p-0.5 -m-0.5 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 cursor-pointer transition-colors"
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                          {format(new Date(order.pickup_datetime), 'MM/dd HH:mm')}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-1">
                          {getCategoryBadge(order.order_category)}
                          <span className="shrink-0 whitespace-nowrap" title={order.order_number}>#{order.order_number.slice(-4)}</span>
                          {order.source === 'customer_booking' && (
                            <span className="shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">客訂</span>
                          )}
                          {order.edited_by_customer && (
                            <span className="shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">已修改</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 右邊：金額 + 狀態（垂直排列）*/}
                    <div className="flex flex-col items-end gap-1">
                      {/* 付款方式 + 金額 */}
                      {!(isDriver && tenantDocTemplates.hideAmount) && (
                        <div className="flex items-center gap-1.5">
                          {order.payment_method && getPaymentBadge(order.payment_method)}
                          <span className="font-bold text-lg text-blue-600 dark:text-blue-400">${Math.round(order.pricing.total_amount).toLocaleString()}</span>
                        </div>
                      )}

                      {/* 司機款項：付款給司機（跟客戶付款方式無關） */}
                      {!isDriver && (
                        <div onClick={e => e.stopPropagation()}>
                          {order.driver_paid_at ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="w-3 h-3" />
                              司機款 {format(new Date(order.driver_paid_at), 'MM/dd')}
                            </span>
                          ) : order.payment_method !== '現金' ? (
                            <button
                              type="button"
                              onClick={() => handleMarkDriverPaid(order)}
                              className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors cursor-pointer"
                            >
                              付款給司機
                            </button>
                          ) : null}
                        </div>
                      )}

                      {/* 狀態 + 費用資訊 */}
                      <div className="flex items-center gap-1">
                        {getStatusBadge(order.status)}
                        {tenantDocTemplates.showDriverFeeInfo && ((order.pricing?.driver_fee ?? 0) !== 0 || (order.pricing?.vendor_rebate ?? 0) !== 0) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            ({order.pricing?.driver_fee ?? 0}/{order.pricing?.vendor_rebate ?? 0})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 第4行：乘客資訊（已完成時司機不顯示） */}
                  {!(isDriver && isCompleted) && (
                    <div className="flex items-start justify-between gap-2 text-sm">
                      <div className="flex items-start gap-2">
                        <User className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex flex-col gap-0.5">
                          {order.customer_name && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{order.customer_name.slice(0, 4)}</span>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-900 dark:text-white font-medium text-base">{order.passenger_name}</span>
                            {order.passenger_phone && (
                              <a href={`tel:${order.passenger_phone}`} className="text-blue-600 dark:text-blue-400 text-base hover:underline cursor-pointer">
                                {order.passenger_phone}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 第5行：行程（已完成時司機不顯示） */}
                  {!(isDriver && isCompleted) && (
                    <div className="space-y-1 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-md">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(order.pickup_location)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer text-base">
                          {order.pickup_location}
                        </a>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(order.dropoff_location)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer text-base">
                          {order.dropoff_location}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* 第6行：車輛資訊 + 派出按鈕 */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-base">
                      <Car className="w-4 h-4" />
                      <span>
                        {order.vehicle_type} | {order.passenger_count}人 | {order.luggage_count}行李
                        {order.child_seat > 0 && ` | ${order.child_seat}兒童椅`}
                        {(order.booster_seat ?? 0) > 0 && ` | ${order.booster_seat}增高墊`}
                        {order.flight_number && ` | 航班：${order.flight_number}`}
                      </span>
                    </div>
                    {/* 派出按鈕 - 已派車/進行中時顯示 */}
                    {(order.status === '已派車' || order.status === '進行中') && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          onClick={() => handleViewDispatchDocument(order)}
                          className="h-8 w-8 px-0 bg-blue-500 border-blue-600 text-white hover:bg-blue-600 font-semibold text-xs"
                        >
                          派
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleViewRentalDocument(order)}
                          className="h-8 w-8 px-0 bg-orange-500 border-orange-600 text-white hover:bg-orange-600 font-semibold text-xs"
                        >
                          出
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* 第7行：司機指派 + 更多操作 - 管理員用，同一列比較省空間 */}
                  {!isDriver && (
                    <div className="flex gap-2 items-center">
                    <DriverCombobox
                      drivers={drivers}
                      value={order.driver_id || ''}
                      onChange={(driverId) => handleDriverChange(order.id, driverId)}
                      disabledDriverIds={(() => {
                        const pickupDate = order.pickup_datetime ? format(new Date(order.pickup_datetime), 'yyyy-MM-dd') : ''
                        return pickupDate ? (onLeaveDateMap[pickupDate] || []) : []
                      })()}
                      className="flex-1 min-w-0"
                      triggerClassName="flex h-9 w-full items-center justify-between px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer"
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="shrink-0 px-3 whitespace-nowrap">
                          <MoreHorizontal className="w-4 h-4 mr-1" /> 更多
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" side="top" className="w-52">
                        <DropdownMenuItem onClick={() => handleEdit(order)} className="cursor-pointer">
                          <Edit className="w-4 h-4 mr-2" />
                          編輯
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(buildGoogleCalendarUrl(order), '_blank', 'noopener,noreferrer')} className="cursor-pointer text-red-600 dark:text-red-400">
                          <Calendar className="w-4 h-4 mr-2" />
                          加入 Google 日曆
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTextInfoOrder(order)} className="cursor-pointer">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          訂單文字資訊
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTextWithDriverInfoOrder(order)} className="cursor-pointer">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          訂單+司機資訊
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTextWithDriverInfoForDriverOrder(order)} className="cursor-pointer">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          訂單+司機資訊（給司機）
                        </DropdownMenuItem>
                        {tenantDocTemplates.hasDispatch && (
                          <DropdownMenuItem onClick={() => handleViewDispatchDocument(order)} className="cursor-pointer">
                            <FileImage className="w-4 h-4 mr-2" />
                            檢視派車單
                          </DropdownMenuItem>
                        )}
                        {tenantDocTemplates.hasDispatch && (
                          <DropdownMenuItem onClick={() => handleViewDispatchDocument(order, true)} className="cursor-pointer">
                            <FileImage className="w-4 h-4 mr-2" />
                            檢視派車單（客戶）
                          </DropdownMenuItem>
                        )}
                        {tenantDocTemplates.hasRental && (
                          <DropdownMenuItem onClick={() => handleViewRentalDocument(order)} className="cursor-pointer">
                            <FileImage className="w-4 h-4 mr-2" />
                            檢視出租單
                          </DropdownMenuItem>
                        )}
                        {order.custom_sign_image_path && order.signature_image_path && (
                          <DropdownMenuItem
                            onClick={() => {
                              const filename = order.signature_image_path!.split(/[/\\]/).pop()
                              const a = document.createElement('a')
                              a.href = `/uploads/signatures/${filename}`
                              a.target = '_blank'
                              a.rel = 'noopener noreferrer'
                              document.body.appendChild(a)
                              a.click()
                              document.body.removeChild(a)
                            }}
                            className="cursor-pointer text-purple-600 dark:text-purple-400"
                          >
                            <ImagePlus className="w-4 h-4 mr-2" />
                            客製簽名結果
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => downloadSignBoard(order.id, order.passenger_name)} className="cursor-pointer">
                          <FileText className="w-4 h-4 mr-2" />
                          舉牌檔
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopyDriverInfo(order)} className="cursor-pointer">
                          <Car className="w-4 h-4 mr-2" />
                          司機資訊
                        </DropdownMenuItem>
                        {drivers.find(d => d.id === order.driver_id)?.driver_category === 'external' && (
                          <DropdownMenuItem onClick={() => handleCopyExternalSignLink(order)} className="cursor-pointer text-purple-600 dark:text-purple-400">
                            <Copy className="w-4 h-4 mr-2" />
                            複製外派簽名連結
                          </DropdownMenuItem>
                        )}
                        {order.status !== '已完成' && order.status !== '已取消' && (
                          <DropdownMenuItem onClick={() => handleCopyOrderSignLink(order)} className="cursor-pointer text-purple-600 dark:text-purple-400">
                            <Copy className="w-4 h-4 mr-2" />
                            複製外派簽單連結（免建檔）
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  )}

                  {/* 司機名稱顯示 - 管理員用 */}
                  {!isDriver && order.driver_name && (
                    <div className="space-y-0.5 text-xs">
                      <div className="text-gray-900 dark:text-white font-medium flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {order.driver_name}
                      </div>
                      {order.driver_phone && (
                        <a href={`tel:${order.driver_phone}`} className="text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline cursor-pointer">
                          <Phone className="w-3 h-3" />
                          {order.driver_phone}
                        </a>
                      )}
                      {order.vehicle_number && (
                        <div className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {order.vehicle_number}
                        </div>
                      )}
                      {(order.departed_at || order.picked_up_at || order.completed_at) && (
                        <div className="mt-1 pt-1 border-t border-gray-100 dark:border-gray-700 space-y-0.5">
                          {order.departed_at && (
                            <div className="text-blue-600 dark:text-blue-400">出發 {fmtTime(order.departed_at)}</div>
                          )}
                          {order.picked_up_at && (
                            <div className="text-green-600 dark:text-green-400">客上 {fmtTime(order.picked_up_at)}</div>
                          )}
                          {order.completed_at && (
                            <div className="text-gray-500 dark:text-gray-400">客下 {fmtTime(order.completed_at)}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 第3點上車點 - 如果有的話 */}
                  {order.additional_stops && order.additional_stops.length > 0 && (
                    <div className="space-y-1 text-xs">
                      {order.additional_stops.map((stop, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                          <a href={`https://www.google.com/maps/search/${encodeURIComponent(stop.address)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                            {stop.address}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 備註 - 如果有的話 */}
                  {order.driver_notes && (
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">備註：</span>
                      {renderNotesWithLinks(order.driver_notes)}
                    </div>
                  )}

                  {/* 離線待同步標記 */}
                  {isDriver && getPendingActionsForOrder(order.id).length > 0 && (
                    <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
                      <WifiOff className="h-3 w-3" /> 離線紀錄，待同步
                    </div>
                  )}

                  {/* Actions - 司機版本 */}
                  {isDriver ? (
                  (order.status === '已派車' || order.status === '進行中') && (
                    <div className="flex gap-2 pt-2">
                      {/* 出發按鈕 - 已派車時可按 */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-blue-500 border-blue-600 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                        onClick={() => handleDriverAction(order, '出發')}
                        disabled={actionLoading === order.id || order.status !== '已派車'}
                      >
                        <Play className="w-4 h-4 mr-1" /> 出發
                      </Button>

                      {/* 客上按鈕 - 進行中且未上車時可按 */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-green-500 border-green-600 text-white hover:bg-green-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                        onClick={() => handleDriverAction(order, '客上')}
                        disabled={actionLoading === order.id || order.status !== '進行中' || !!order.picked_up_at}
                      >
                        <UserCheck className="w-4 h-4 mr-1" /> 客上
                      </Button>

                      {/* 簽名按鈕 - 有簽單、客製簽單、或選了迅安簽單樣板時顯示 */}
                      {(order.is_signed || order.custom_sign_image_path || order.dispatch_template_key === 'xunan') && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 bg-purple-500 border-purple-600 text-white hover:bg-purple-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                          onClick={() => handleViewSignature(order)}
                          disabled={actionLoading === order.id || !order.picked_up_at || order.status.includes('完成') || !!order.signature_image_path}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> 簽名
                        </Button>
                      )}

                      {/* 客下按鈕 - 上車後可按 */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-orange-500 border-orange-600 text-white hover:bg-orange-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                        onClick={() => handleDriverAction(order, '客下')}
                        disabled={actionLoading === order.id || !order.picked_up_at || order.status.includes('完成')}
                      >
                        <LogOut className="w-4 h-4 mr-1" /> 客下
                      </Button>
                    </div>
                  )
                ) : null}
                </div>
                              )
                            })
                          )}
                        </div>
                        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {!isDriver && (
                  <th className="px-4 py-3 w-[50px]">
                    <Checkbox
                      checked={sortedOrders.length > 0 && selectedOrders.length === sortedOrders.length}
                      onCheckedChange={handleToggleSelectAll}
                    />
                  </th>
                )}
                {isDriver && <th className="px-4 py-3 w-[50px]" />}
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[160px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 select-none"
                  onClick={() => handleSort('date')}
                >
                  訂單資訊 <SortIcon k="date" />
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 select-none"
                  onClick={() => handleSort('status')}
                >
                  狀態 <SortIcon k="status" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[160px]">
                  乘客資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[300px]">
                  行程
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 select-none"
                  onClick={() => handleSort('amount')}
                >
                  金額 <SortIcon k="amount" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  司機
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <TableLoading colSpan={8} message="正在加載訂單..." />
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <ClipboardList className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">目前沒有訂單資料</p>
                  </td>
                </tr>
              ) : (
                sortedOrders.map((order) => {
                  const isSelected = selectedOrders.includes(order.id)
                  return (
                    <tr 
                      key={order.id} 
                      className={cn(
                        "transition-all duration-200",
                        getRowStyles(order.status, isSelected),
                        !isSelected && "hover:brightness-95 dark:hover:brightness-110"
                      )}
                    >
                      {!isDriver && (
                        <td className="px-4 py-4">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleToggleSelectOne(order.id)}
                          />
                        </td>
                      )}
                      {isDriver && <td className="px-4 py-4" />}
                    <td
                      className={cn("px-6 py-4", !isDriver && "cursor-pointer select-none")}
                      title={!isDriver ? '點兩下開啟編輯' : undefined}
                      onDoubleClick={!isDriver ? () => handleEdit(order) : undefined}
                    >
                      <div className="text-base font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(order.pickup_datetime), 'yyyy/MM/dd HH:mm')}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-1">
                        {getCategoryBadge(order.order_category)}
                        <span className="shrink-0 whitespace-nowrap" title={order.order_number}>#{order.order_number.slice(-4)}</span>
                        {order.source === 'customer_booking' && (
                          <span className="shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">客訂</span>
                        )}
                        {order.edited_by_customer && (
                          <span className="shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">已修改</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4">
                      {order.customer_name && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{order.customer_name.slice(0, 4)}</div>
                      )}
                      <div className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {order.passenger_name}
                      </div>
                      <a href={`tel:${order.passenger_phone}`} className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-1 hover:underline cursor-pointer">
                        <Phone className="w-3 h-3" />
                        {order.passenger_phone}
                      </a>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {order.passenger_count} 人 | {order.vehicle_type}{order.flight_number ? ` | ${order.flight_number}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a href={`https://www.google.com/maps/search/${encodeURIComponent(order.pickup_location)}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 flex items-start gap-1 hover:underline cursor-pointer">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <div className="line-clamp-2">{order.pickup_location}</div>
                      </a>
                      <a href={`https://www.google.com/maps/search/${encodeURIComponent(order.dropoff_location)}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 dark:text-gray-400 flex items-start gap-1 mt-1 hover:underline cursor-pointer">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <div className="line-clamp-2">{order.dropoff_location}</div>
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {!(isDriver && tenantDocTemplates.hideAmount) && (
                      <div className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        {Math.round(order.pricing.total_amount).toLocaleString()}
                      </div>
                      )}
                      {!isDriver && tenantDocTemplates.showDriverFeeInfo && order.pricing.total_amount > 0 && ((order.pricing?.driver_fee ?? 0) !== 0 || (order.pricing?.vendor_rebate ?? 0) !== 0) && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {order.pricing?.driver_fee ?? 0}/{order.pricing?.vendor_rebate ?? 0}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {order.payment_method}
                      </div>
                      {!isDriver && (
                        <div className="mt-1">
                          {order.driver_paid_at ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="w-3 h-3" />
                              司機款 {format(new Date(order.driver_paid_at), 'MM/dd')}
                            </span>
                          ) : order.payment_method !== '現金' ? (
                            <button
                              type="button"
                              onClick={() => handleMarkDriverPaid(order)}
                              className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors cursor-pointer"
                            >
                              付款給司機
                            </button>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {!isDriver && (
                        <DriverCombobox
                          drivers={drivers}
                          value={order.driver_id || ''}
                          onChange={(driverId) => handleDriverChange(order.id, driverId)}
                          disabledDriverIds={(() => {
                            const pickupDate = order.pickup_datetime ? format(new Date(order.pickup_datetime), 'yyyy-MM-dd') : ''
                            return pickupDate ? (onLeaveDateMap[pickupDate] || []) : []
                          })()}
                          triggerClassName="flex h-10 w-full items-center justify-between px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                        />
                      )}
                      {order.driver_name && (
                        <div className="mt-2 space-y-1">
                          <div className="text-xs text-gray-900 dark:text-white font-medium flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {order.driver_name}
                          </div>
                          {order.driver_phone && (
                            <a href={`tel:${order.driver_phone}`} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline cursor-pointer">
                              <Phone className="w-3 h-3" />
                              {order.driver_phone}
                            </a>
                          )}
                          {order.vehicle_number && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Car className="w-3 h-3" />
                              {order.vehicle_number}
                            </div>
                          )}
                          {(order.departed_at || order.picked_up_at || order.completed_at) && (
                            <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 space-y-0.5">
                              {order.departed_at && (
                                <div className="text-xs text-blue-600 dark:text-blue-400">
                                  出發 {fmtTime(order.departed_at)}
                                </div>
                              )}
                              {order.picked_up_at && (
                                <div className="text-xs text-green-600 dark:text-green-400">
                                  客上 {fmtTime(order.picked_up_at)}
                                </div>
                              )}
                              {order.completed_at && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  客下 {fmtTime(order.completed_at)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {/* 司機版本 */}
                      {isDriver ? (
                        (order.status === '已派車' || order.status === '進行中') && (
                          <div className="flex items-center justify-end gap-1">
                            {/* 出發按鈕 - 已派車時可按 */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 bg-blue-500 border-blue-600 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                              onClick={() => handleDriverAction(order, '出發')}
                              disabled={actionLoading === order.id || order.status !== '已派車'}
                            >
                              <Play className="w-3 h-3 mr-1" /> 出發
                            </Button>

                            {/* 客上按鈕 - 進行中且未上車時可按 */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 bg-green-500 border-green-600 text-white hover:bg-green-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                              onClick={() => handleDriverAction(order, '客上')}
                              disabled={actionLoading === order.id || order.status !== '進行中' || !!order.picked_up_at}
                            >
                              <UserCheck className="w-3 h-3 mr-1" /> 客上
                            </Button>

                            {/* 簽名按鈕 - 有簽單、客製簽單、或選了迅安簽單樣板時顯示 */}
                            {(order.is_signed || order.custom_sign_image_path || order.dispatch_template_key === 'xunan') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 bg-purple-500 border-purple-600 text-white hover:bg-purple-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                onClick={() => handleViewSignature(order)}
                                disabled={actionLoading === order.id || !order.picked_up_at || order.status.includes('完成') || !!order.signature_image_path}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> 簽名
                              </Button>
                            )}

                            {/* 客下按鈕 - 上車後可按 */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 bg-orange-500 border-orange-600 text-white hover:bg-orange-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                              onClick={() => handleDriverAction(order, '客下')}
                              disabled={actionLoading === order.id || !order.picked_up_at || order.status.includes('完成')}
                            >
                              <LogOut className="w-3 h-3 mr-1" /> 客下
                            </Button>
                          </div>
                        )
                      ) : (
                      /* 管理員版本 */
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="inline-flex items-center p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" side="top" className="w-44">
                          <DropdownMenuItem onClick={() => handleEdit(order)} className="cursor-pointer">
                            <Edit className="w-4 h-4 mr-2" />
                            編輯
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(buildGoogleCalendarUrl(order), '_blank', 'noopener,noreferrer')} className="cursor-pointer text-red-600 dark:text-red-400">
                            <Calendar className="w-4 h-4 mr-2" />
                            加入 Google 日曆
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTextInfoOrder(order)} className="cursor-pointer">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            訂單文字資訊
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTextWithDriverInfoOrder(order)} className="cursor-pointer">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            訂單+司機資訊
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTextWithDriverInfoForDriverOrder(order)} className="cursor-pointer">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            訂單+司機資訊（給司機）
                          </DropdownMenuItem>
                          {tenantDocTemplates.hasDispatch && (
                            <DropdownMenuItem onClick={() => handleViewDispatchDocument(order)} className="cursor-pointer">
                              <FileImage className="w-4 h-4 mr-2" />
                              檢視派車單
                            </DropdownMenuItem>
                          )}
                          {tenantDocTemplates.hasDispatch && (
                            <DropdownMenuItem onClick={() => handleViewDispatchDocument(order, true)} className="cursor-pointer">
                              <FileImage className="w-4 h-4 mr-2" />
                              檢視派車單（客戶）
                            </DropdownMenuItem>
                          )}
                          {tenantDocTemplates.hasRental && (
                            <DropdownMenuItem onClick={() => handleViewRentalDocument(order)} className="cursor-pointer">
                              <FileImage className="w-4 h-4 mr-2" />
                              檢視出租單
                            </DropdownMenuItem>
                          )}
                          {order.custom_sign_image_path && order.signature_image_path && (
                            <DropdownMenuItem
                              onClick={() => {
                                const filename = order.signature_image_path!.split(/[/\\]/).pop()
                                window.open(`/uploads/signatures/${filename}`, '_blank')
                              }}
                              className="cursor-pointer text-purple-600 dark:text-purple-400"
                            >
                              <ImagePlus className="w-4 h-4 mr-2" />
                              客製簽名結果
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => downloadSignBoard(order.id, order.passenger_name)} className="cursor-pointer">
                            <FileText className="w-4 h-4 mr-2" />
                            舉牌檔
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCopyDriverInfo(order)} className="cursor-pointer">
                            <Car className="w-4 h-4 mr-2" />
                            司機資訊
                          </DropdownMenuItem>
                          {drivers.find(d => d.id === order.driver_id)?.driver_category === 'external' && (
                            <DropdownMenuItem onClick={() => handleCopyExternalSignLink(order)} className="cursor-pointer text-purple-600 dark:text-purple-400">
                              <Copy className="w-4 h-4 mr-2" />
                              複製外派簽名連結
                            </DropdownMenuItem>
                          )}
                          {order.status !== '已完成' && order.status !== '已取消' && (
                            <DropdownMenuItem onClick={() => handleCopyOrderSignLink(order)} className="cursor-pointer text-purple-600 dark:text-purple-400">
                              <Copy className="w-4 h-4 mr-2" />
                              複製外派簽單連結（免建檔）
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && (searchTerm ? total : backendTotal) > 10 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <span>共 {searchTerm ? total : backendTotal} 筆訂單</span>
              <label className="flex items-center gap-1">
                每頁
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                  className="px-1.5 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white cursor-pointer"
                >
                  {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                筆
              </label>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                上一頁
              </button>
              {getPageNumbers(page, Math.max(1, Math.ceil((searchTerm ? total : backendTotal) / pageSize))).map((p, idx) =>
                p === '…' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-sm text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={cn(
                      "min-w-[2rem] px-2 py-1 text-sm border rounded cursor-pointer transition-colors",
                      p === page
                        ? "border-blue-500 bg-blue-500 text-white font-bold"
                        : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    )}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page * pageSize >= backendTotal}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 訂單文字資訊 Modal */}
      {textInfoOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-sm text-gray-900 dark:text-white">訂單文字資訊</span>
                <span className="text-xs text-gray-400">{textInfoOrder.order_category}</span>
              </div>
              <button onClick={() => { setTextInfoOrder(null); setTextCopied(false) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                {generateOrderText(textInfoOrder)}
              </pre>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleCopyText(generateOrderText(textInfoOrder), () => { setTextInfoOrder(null) })}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white py-2 text-sm font-medium transition-colors"
              >
                {textCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {textCopied ? '已複製！' : '複製文字'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 訂單+司機資訊（給司機）Modal */}
      {textWithDriverInfoForDriverOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-sm text-gray-900 dark:text-white">訂單+司機資訊（給司機）</span>
                <span className="text-xs text-gray-400">{textWithDriverInfoForDriverOrder.order_category}</span>
              </div>
              <button onClick={() => { setTextWithDriverInfoForDriverOrder(null); setTextCopied(false) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                {generateOrderWithDriverTextForDriver(textWithDriverInfoForDriverOrder)}
              </pre>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleCopyText(generateOrderWithDriverTextForDriver(textWithDriverInfoForDriverOrder), () => { setTextWithDriverInfoForDriverOrder(null) })}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white py-2 text-sm font-medium transition-colors"
              >
                {textCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {textCopied ? '已複製！' : '複製文字'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 訂單+司機資訊 Modal */}
      {textWithDriverInfoOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-sm text-gray-900 dark:text-white">訂單+司機資訊</span>
                <span className="text-xs text-gray-400">{textWithDriverInfoOrder.order_category}</span>
              </div>
              <button onClick={() => { setTextWithDriverInfoOrder(null); setTextCopied(false) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                {generateOrderWithDriverText(textWithDriverInfoOrder)}
              </pre>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleCopyText(generateOrderWithDriverText(textWithDriverInfoOrder), () => { setTextWithDriverInfoOrder(null) })}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white py-2 text-sm font-medium transition-colors"
              >
                {textCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {textCopied ? '已複製！' : '複製文字'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 司機資訊 Modal */}
      {driverInfoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-sm text-gray-900 dark:text-white">司機資訊</span>
                <span className="text-xs text-gray-400">{driverInfoModal.order.order_category}</span>
              </div>
              <button onClick={() => { setDriverInfoModal(null); setTextCopied(false) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                {driverInfoModal.text}
              </pre>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleCopyText(driverInfoModal.text, () => { setDriverInfoModal(null) })}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white py-2 text-sm font-medium transition-colors"
              >
                {textCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {textCopied ? '已複製！' : '複製文字'}
              </button>
            </div>
          </div>
        </div>
      )}

      <OrderDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setAiParsedOrder(null)
          setAiRawText('')
          if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl)
            setImagePreviewUrl(undefined)
          }
          // If was editing from bulk list, re-open bulk drawer
          if ((selectedOrder as any)?._editIndex !== undefined) {
            setIsBulkDrawerOpen(true)
          }
          setSelectedOrder(null)
        }}
        onSubmit={handleSubmit}
        onSaveAsNew={handleSaveAsNew}
        onSaved={handleOrderSaved}
        order={selectedOrder || (aiParsedOrder as any)}
        drivers={drivers}
        customers={customers}
        onRequestAddCustomer={() => setIsQuickAddCustomerOpen(true)}
        autoSelectCustomerId={quickAddedCustomerId}
        previewImageUrl={imagePreviewUrl}
        aiRawText={aiRawText}
        onCustomSignFileChange={setPendingCustomSignFile}
      />

      <SaveResultToast
        items={saveToastItems}
        onOpen={handleOpenSavedOrder}
        onClose={() => setSaveToastItems([])}
      />

      <CustomerDrawer
        isOpen={isQuickAddCustomerOpen}
        onClose={() => setIsQuickAddCustomerOpen(false)}
        onSubmit={handleQuickAddCustomer}
        tenants={EMPTY_TENANTS}
        elevated
      />

      {/* AI Order Drawer */}
      <AIOrderDrawer
        isOpen={isAIDrawerOpen}
        onClose={() => setIsAIDrawerOpen(false)}
        onSingleOrder={handleAISingleOrder}
        onMultipleOrders={handleAIMultipleOrders}
      />

      {/* Bulk Orders Confirm Drawer */}
      <BulkOrdersConfirmDrawer
        isOpen={isBulkDrawerOpen}
        onClose={() => setIsBulkDrawerOpen(false)}
        orders={bulkOrders}
        onEditOrder={handleAIEditOrder}
        onSuccess={handleBulkSuccess}
        rawText={aiRawText}
      />

      {/* Document Modal */}
      <DocumentModal
        isOpen={isDocumentModalOpen}
        onClose={() => {
          setIsDocumentModalOpen(false)
          setDocumentModalOrder(null)
        }}
        order={documentModalOrder}
        type={documentModalType}
        forceAmount={documentModalForceAmount}
      />

      {/* Signature Modal - 司機簽名 */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => {
          setIsSignatureModalOpen(false)
          setSignatureOrder(null)
          setActionLoading(null)
        }}
        onSave={handleSaveSignature}
        orderNumber={signatureOrder?.order_number || ''}
        customSignImageUrl={
          signatureOrder?.custom_sign_image_path
            ? `/uploads/custom_signs/${signatureOrder.custom_sign_image_path.split(/[/\\]/).pop()}`
            : undefined
        }
      />

      <ConfirmDialog
        open={!!driverPayConfirmOrder}
        title="付款給司機"
        message={
          driverPayConfirmOrder
            ? `確認已將司機費用 $${Math.round(driverPayConfirmOrder.pricing?.driver_fee || 0).toLocaleString()} 付給 ${driverPayConfirmOrder.driver_name || '司機'} 嗎？`
            : ''
        }
        confirmText="確認已付款"
        loading={driverPaySubmitting}
        onConfirm={handleConfirmDriverPaid}
        onCancel={() => setDriverPayConfirmOrder(null)}
      />

      {/* Floating Action Buttons - 只顯示給非司機用戶 */}
      {!isDriver && (
        <div className="fixed z-40 bottom-20 right-4 lg:bottom-10 lg:right-10 flex flex-col gap-3 items-end">
          {/* Image Parse Button */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageParse}
          />
          {/* 圖片上傳 FAB 暫時隱藏 */}
          {false && (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full shadow ${lastOrderMode === 'image' ? 'bg-violet-600 text-white' : 'bg-white/80 text-violet-600 border border-violet-200'}`}>上傳</span>
            <Button
              onClick={() => { setOrderMode('image'); imageInputRef.current?.click() }}
              disabled={imageParseLoading}
              className={`h-14 w-14 rounded-full shadow-xl bg-gradient-to-br from-violet-500 to-violet-700 hover:from-violet-600 hover:to-violet-800 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-70 ${lastOrderMode === 'image' ? 'ring-4 ring-violet-300' : ''}`}
              title="圖片辨識建單"
            >
              {imageParseLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
            </Button>
          </div>
          )}

          {/* AI Order Button */}
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full shadow ${lastOrderMode === 'ai' ? 'bg-orange-500 text-white' : 'bg-white/80 text-orange-500 border border-orange-200'}`}>文字</span>
            <Button
              onClick={() => { setOrderMode('ai'); setIsAIDrawerOpen(true) }}
              className={`h-14 w-14 rounded-full shadow-xl bg-gradient-to-br from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${lastOrderMode === 'ai' ? 'ring-4 ring-orange-300' : ''}`}
              title="AI 智能建單"
            >
              <Sparkles className="h-6 w-6" />
            </Button>
          </div>

          {/* Manual Create Button */}
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full shadow ${lastOrderMode === 'form' ? 'bg-blue-600 text-white' : 'bg-white/80 text-blue-600 border border-blue-200'}`}>表單</span>
            <Button
              onClick={() => { setOrderMode('form'); handleCreate() }}
              className={`h-14 w-14 rounded-full shadow-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${lastOrderMode === 'form' ? 'ring-4 ring-blue-300' : ''}`}
              title="手動建單"
            >
              <Plus className="h-8 w-8" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
